import React, { useState, useCallback, useEffect, useRef } from "react";
import { RowConfig } from "../types";
import styles from "../styles/Verifier.module.css";
import { GameVerificationResult, verifyGame } from "../services/provablyFair";

interface VerifierProps {
  initialGameConfig?: string;
  initialSeed?: string;
  initialSeedHash?: string;
}

const Verifier: React.FC<VerifierProps> = ({
  initialGameConfig,
  initialSeed,
  initialSeedHash,
}) => {
  const [seed, setSeed] = useState("");
  const [seedHash, setSeedHash] = useState("");
  const [gameConfig, setGameConfig] = useState(
    "7,6,5,4,3,4,5,6,7,6,5,4,3,4,5,6,7,6,5,4,3,4,5,6,7",
  );
  const [verificationResult, setVerificationResult] =
    useState<GameVerificationResult | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  // Pre-fill form with initial values when component mounts or props change
  useEffect(() => {
    if (initialGameConfig) {
      setGameConfig(initialGameConfig);
    }
    if (initialSeed) {
      setSeed(initialSeed);
    }
    if (initialSeedHash) {
      setSeedHash(initialSeedHash);
    }
  }, [initialGameConfig, initialSeed, initialSeedHash]);

  // Auto-scroll to result when verification completes
  useEffect(() => {
    if (verificationResult && resultRef.current) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [verificationResult]);

  const parseGameConfig = (config: string): RowConfig[] => {
    return config
      .split(",")
      .map((tiles) => ({ tiles: parseInt(tiles.trim()) }));
  };

  const handleVerify = useCallback(async () => {
    if (!seed || !seedHash || !gameConfig) {
      alert("Please fill in all required fields");
      return;
    }

    setIsVerifying(true);

    try {
      // Parse game configuration
      const rowConfigs = parseGameConfig(gameConfig);
      const result = await verifyGame(seed, rowConfigs, seedHash);
      setVerificationResult(result);
    } catch (error) {
      console.error("Verification error:", error);
      alert("Error during verification");
    } finally {
      setIsVerifying(false);
    }
  }, [seed, seedHash, gameConfig]);

  const handleClear = () => {
    setSeed("");
    setSeedHash("");
    setGameConfig("7,6,5,4,3,4,5,6,7,6,5,4,3,4,5,6,7,6,5,4,3,4,5,6,7");
    setVerificationResult(null);
  };

  return (
    <div className={styles.verifierContainer}>
      <div className={styles.verifierContent}>
        <h1>🔍 Game Verifier</h1>
        <p className={styles.verifierDescription}>
          Verify the fairness of any DeathX Fun game by checking the seed and
          hash
        </p>

        <div className={styles.inputSection}>
          <div className={styles.inputGroup}>
            <label htmlFor="game-config">Row Tiles Count *</label>
            <input
              type="text"
              id="game-config"
              value={gameConfig}
              onChange={(e) => setGameConfig(e.target.value)}
              placeholder="Comma-separated tiles per row (e.g., 7,6,5,4,3)"
              required
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="seed">Game Seed *</label>
            <input
              type="text"
              id="seed"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder="Enter the revealed game seed"
              required
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="seed-hash">Game Hash *</label>
            <input
              type="text"
              id="seed-hash"
              value={seedHash}
              onChange={(e) => setSeedHash(e.target.value)}
              placeholder="Enter the original seed hash"
              required
            />
          </div>

          <div className={styles.buttonGroup}>
            <button
              onClick={handleVerify}
              disabled={isVerifying || !seed || !seedHash || !gameConfig}
              className={`${styles.btn} ${styles.btnPrimary}`}
            >
              {isVerifying ? "Verifying..." : "🔍 Verify Game"}
            </button>
            <button onClick={handleClear} className={`${styles.btn} ${styles.btnSecondary}`}>
              Clear All
            </button>
          </div>
        </div>

        {verificationResult && (
          <div
            ref={resultRef}
            className={`${styles.verificationResult} ${verificationResult.valid ? styles.valid : styles.invalid}`}
          >
            <div className={styles.verificationResultHeader}>
              <h3>
                {verificationResult.valid
                  ? "✅ Verification Successful"
                  : "❌ Verification Failed"}
              </h3>
              <p>
                {verificationResult.valid
                  ? "The seed hash matches! The game was provably fair."
                  : "The seed hash does not match! This indicates potential tampering."}
              </p>
            </div>

            <div className={styles.hashComparison}>
              <div className={styles.verificationHashRow}>
                <span className={styles.hashLabel}>Provided Hash:</span>
                <span className={styles.hashValue}>{seedHash}</span>
              </div>
              <div className={styles.verificationHashRow}>
                <span className={styles.hashLabel}>Calculated Hash:</span>
                <span className={styles.hashValue}>
                  {verificationResult.commitmentHash}
                </span>
              </div>
            </div>

            {verificationResult.valid && (
              <div className={styles.deathTilesSection}>
                <h4>Death Tile Positions</h4>
                <div className={styles.gameGrid}>
                  {verificationResult.deathTiles?.map((deathTile, rowIndex) => (
                    <div key={rowIndex} className={styles.gridRow}>
                      <div className={styles.rowInfo}>
                        <span className={styles.rowNumber}>Row {rowIndex + 1}</span>
                        <span className={styles.deathPosition}>
                          Death: {deathTile.position + 1}
                        </span>
                      </div>
                      <div className={styles.tilesGrid}>
                        {Array.from(
                          { length: deathTile.totalTiles },
                          (_, tileIndex) => (
                            <div
                              key={tileIndex}
                              className={`${styles.gridTile} ${tileIndex === deathTile.position ? styles.deathTile : styles.safeTile}`}
                            >
                              {tileIndex === deathTile.position ? "💀" : ""}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Verifier;
